import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSignedDownloadUrl,
  getGlobalList,
  getProjectList,
  uploadGlobal,
  uploadProject,
  ProgressCallback,
} from './requests';
import { FileManagementRequest, FileManagement, FileSignedDownloadUrl, QueryKey } from 'api/model';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.FileManagement;

const useFileManagement = (
  projectId: string | null = null,
  includeGlobalFiles = false,
  progressCallback?: ProgressCallback,
  enabled = true,
) => {
  const queryClient = useQueryClient();

  const queryFn = () => (projectId === null ? getGlobalList() : getProjectList(projectId, includeGlobalFiles));
  const { data: files, isLoading } = useQuery<FileManagement[], Error>({
    queryKey: [queryKey, { projectId, includeGlobalFiles }],
    queryFn: queryFn,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });

  const uploadFn = (model: FileManagementRequest) => {
    if (model.isGlobal === true || projectId === null) {
      return uploadGlobal(model, progressCallback);
    } else {
      return uploadProject(projectId, model, progressCallback);
    }
  };
  const upload = useMutation({
    mutationFn: uploadFn,
    onSuccess: (data: FileManagement) => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      if (data.meta?.validation_result?.valid) {
        toast.success('File successfully uploaded');
      } else {
        toast.warning('File successfully uploaded with warnings');
      }
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 413) {
        toast.error('File is too large. The maximum file size is 500MB.');
      } else {
        errorHandler('File upload failed')(error);
      }
    },
  });

  const download = useMutation({
    mutationFn: (fileId: string) => getSignedDownloadUrl(fileId),
    onSuccess: (response: FileSignedDownloadUrl) => {
      const downloadUrl = response.url;

      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      toast.success('File download started');
    },
    onError: errorHandler('File download failed'),
  });

  const preview = useMutation({
    mutationFn: (fileId: string) => getSignedDownloadUrl(fileId),
    onSuccess: (response: FileSignedDownloadUrl) => {
      const previewUrl = response.url + '&preview=true';
      window.open(previewUrl, '_blank');
    },
    onError: errorHandler('Failed to preview file'),
  });

  return { files, isLoading, upload, download, preview };
};

export { queryKey };
export default useFileManagement;
